import { useState, useRef, useCallback, useEffect } from 'react';
import getApi from '../services/GetApi';

interface ApiKeyRequest {
  status?: string;
}

interface UserApiKeyState {
  userApiKey: string | null;
  userResetApiKey: any[];
  apiKeyRequest: ApiKeyRequest;
  isPassVisible: boolean;
  copied: boolean;
  apiKeyRef: React.RefObject<HTMLSpanElement>;
  isStandard: boolean;
  isExternal: boolean;
  apiKeyDisplayStatus: 'available' | 'standard' | 'external' | 'unset';
  togglePassVisibility: () => void;
  handleResetApiKeyData: (event: React.FormEvent<HTMLFormElement>) => void;
  handleCopy: () => void;
  handleDeleteUserApiKeyData: () => void;
  setApiKeyRequest: (request: ApiKeyRequest) => void;
  setUserResetApiKey: (data: any[]) => void;
  setUserApiKey: (key: string | null) => void;
}

interface UseUserApiKeyParams {
  user: any;
  userApiKey: string | null;
  setUserApiKey: (key: string | null) => void;
}

/**
 * Custom hook to manage user API key operations.
 * Handles fetching, resetting, copying, and display of API keys.
 *
 * Manages:
 * - API key retrieval and validation
 * - Reset form submission and password validation
 * - Copy-to-clipboard functionality
 * - Visibility toggle for password field
 * - Request status tracking
 */
function normalizeApiKey(value: string | string[] | null | undefined): string | null {
  if (value == null) return null;
  if (Array.isArray(value)) return value.length ? value[0] || null : null;
  return value || null;
}

export function useUserApiKey({
  user,
  userApiKey: contextApiKey,
  setUserApiKey: contextSetUserApiKey,
}: UseUserApiKeyParams): UserApiKeyState {
  const [userApiKey, setUserApiKey] = useState<string | null>(() => normalizeApiKey(contextApiKey));
  const [userResetApiKey, setUserResetApiKey] = useState<any[]>([]);
  const [apiKeyRequest, setApiKeyRequest] = useState<ApiKeyRequest>({});
  const [isPassVisible, setIsPassVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const apiKeyRef = useRef<HTMLSpanElement>(null);

  // Sincronizar estado local cuando el contexto se actualiza (p. ej. tras reset)
  useEffect(() => {
    const next = normalizeApiKey(contextApiKey);
    if (next !== userApiKey) setUserApiKey(next);
  }, [contextApiKey]);

  // Determine if user is standard tier (limited features)
  const isStandard =
    Array.isArray(user?.['cognito:groups']) && user['cognito:groups'].includes('ROLE_STANDARD');

  // Determine if user is from external identity provider
  const isExternal = user.identities ? true : false;

  // Determine display status based on user state
  const apiKeyDisplayStatus: 'available' | 'standard' | 'external' | 'unset' = isStandard
    ? 'standard'
    : userApiKey
      ? 'available'
      : isExternal
        ? 'external'
        : 'unset';

  // Fetch API key on component mount or when user changes
  useEffect(() => {
    const userId = user?.['cognito:username'] ?? user?.sub;
    if (!user || !userId) {
      return;
    }

    const fetchedApiKey = getApi(userId).then(response => {
      if (response?.status === 'OK') {
        return response?.payload['api_key'];
      }
    });

    Promise.resolve(fetchedApiKey).then(apiKey => {
      if (apiKey !== userApiKey) {
        setUserApiKey(apiKey);
        contextSetUserApiKey(apiKey);
      }
    });
  }, [contextApiKey, user, userApiKey]);

  const togglePassVisibility = useCallback(() => {
    setIsPassVisible(prevState => !prevState);
  }, []);

  const handleResetApiKeyData = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // Usar el mismo identificador que GetApi (cognito:username) para que backend get y reset coincidan
      const userId = user?.['cognito:username'] ?? user?.sub;
      const formData = new FormData(event.currentTarget);
      const password = formData.get('password') as string;

      const userData = [userId, password];
      setUserResetApiKey(userData);
    },
    [user],
  );

  const handleCopy = useCallback(() => {
    const apiKeyText = apiKeyRef.current?.innerText;
    if (apiKeyText) {
      navigator.clipboard.writeText(apiKeyText);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 10000);
    }
  }, []);

  const handleDeleteUserApiKeyData = useCallback(() => {
    setApiKeyRequest({});
    setUserResetApiKey([]);
    const resetForm = document.getElementById('resetForm') as HTMLFormElement;
    resetForm?.reset();
  }, []);

  return {
    userApiKey,
    userResetApiKey,
    apiKeyRequest,
    isPassVisible,
    copied,
    apiKeyRef,
    isStandard,
    isExternal,
    apiKeyDisplayStatus,
    togglePassVisibility,
    handleResetApiKeyData,
    handleCopy,
    handleDeleteUserApiKeyData,
    setApiKeyRequest,
    setUserResetApiKey,
    setUserApiKey,
  };
}
